import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useEffectiveUser } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaInfoCircle } from 'react-icons/fa';
import { FiEdit2, FiTrash2, FiLoader } from 'react-icons/fi';
import { CURRENCY_OPTIONS } from '../utils/currencySymbols';
import ConfirmationModal from '../components/ConfirmationModal';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

function Settings() {
  const navigate = useNavigate();
  const { currentUser } = useEffectiveUser();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    theme: 'light',
    currencies: ['GBP'], // Changed from single currency to array
    language: 'en-GB',
    email: currentUser?.email,
  });
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isAddingCategory, setIsAddingCategory] = useState(false);

  // Load settings and categories on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch settings
        const settingsDoc = await getDoc(doc(db, 'userSettings', currentUser.uid));
        if (settingsDoc.exists()) {
          const userSettings = settingsDoc.data();
          setSettings(userSettings);
          document.documentElement.className = userSettings.theme;
        } else {
          const defaultSettings = {
            theme: 'light',
            currencies: ['GBP'],
            language: 'en-GB',
            email: currentUser?.email,
          };
          await setDoc(doc(db, 'userSettings', currentUser.uid), defaultSettings);
          setSettings(defaultSettings);
        }

        // Fetch categories
        const categoriesDoc = await getDoc(doc(db, 'categories', currentUser.uid));
        if (categoriesDoc.exists()) {
          setCategories(categoriesDoc.data().items || []);
        }
      } catch (error) {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const handleSettingChange = async (setting, value) => {
    try {
      const newSettings = { ...settings, [setting]: value };
      await setDoc(doc(db, 'userSettings', currentUser.uid), newSettings);
      setSettings(newSettings);
      
      // Apply theme change
      if (setting === 'theme') {
        document.documentElement.className = value;
      }
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (isAddingCategory) return;
    
    const trimmedCategory = newCategory.trim();
    
    if (!trimmedCategory) {
      toast.error('Category name cannot be empty');
      return;
    }

    if (categories.includes(trimmedCategory)) {
      toast.error('Category already exists');
      return;
    }

    setIsAddingCategory(true);
    try {
      const newCategories = [...categories, trimmedCategory].sort();
      await setDoc(doc(db, 'categories', currentUser.uid), {
        items: newCategories
      });
      setCategories(newCategories);
      setNewCategory('');
      toast.success('Category added successfully');
    } catch (error) {
      toast.error('Failed to add category');
    } finally {
      setIsAddingCategory(false);
    }
  };

  const handleEditCategory = async (oldValue, newValue) => {
    const trimmedValue = newValue.trim();
    
    if (!trimmedValue) {
      toast.error('Category name cannot be empty');
      return;
    }

    if (trimmedValue === oldValue) {
      setEditingCategory(null);
      return;
    }

    if (categories.includes(trimmedValue)) {
      toast.error('Category already exists');
      return;
    }

    try {
      const newCategories = categories
        .map(cat => cat === oldValue ? trimmedValue : cat)
        .sort();
      
      await setDoc(doc(db, 'categories', currentUser.uid), {
        items: newCategories
      });
      setCategories(newCategories);
      setEditingCategory(null);
      toast.success('Category updated successfully');
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDeleteCategory = async (category) => {
    setCategoryToDelete(category);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const newCategories = categories.filter(cat => cat !== categoryToDelete);
      await setDoc(doc(db, 'categories', currentUser.uid), {
        items: newCategories
      });
      setCategories(newCategories);
      toast.success('Category deleted successfully');
    } catch (error) {
      toast.error('Failed to delete category');
    } finally {
      setIsConfirmModalOpen(false);
      setCategoryToDelete(null);
    }
  };

  // Animation variants for Framer Motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gray-50 dark:bg-gray-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <motion.div 
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="flex items-center mb-4 sm:mb-6"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="mr-4 text-gray-600 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <FaArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 space-y-6 sm:space-y-8"
        >
          {/* Theme Setting */}
          <motion.div variants={itemVariants}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Theme
              </label>
              <select
                value={settings.theme}
                onChange={(e) => handleSettingChange('theme', e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>
          </motion.div>

          {/* Currencies Setting */}
          <motion.div variants={itemVariants}>
            <div>
              <label className="block text-sm font-medium mb-2">
                Currencies
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(Select at least one)</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {CURRENCY_OPTIONS.map(currency => (
                  <div 
                    key={currency.value} 
                    className={`
                      flex items-center p-3 rounded-lg border-2 
                      ${settings.currencies.includes(currency.value) 
                        ? 'border-indigo-500 bg-indigo-500/10 dark:bg-indigo-500/20' 
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }
                      transition-colors duration-200 ease-in-out cursor-pointer
                    `}
                    onClick={() => {
                      const newCurrencies = settings.currencies.includes(currency.value)
                        ? settings.currencies.filter(c => c !== currency.value)
                        : [...settings.currencies, currency.value];
                      
                      if (newCurrencies.length === 0) {
                        toast.warning('You must select at least one currency');
                        return;
                      }
                      
                      handleSettingChange('currencies', newCurrencies);
                    }}
                  >
                    <input
                      type="checkbox"
                      id={currency.value}
                      checked={settings.currencies.includes(currency.value)}
                      onChange={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <label 
                      htmlFor={currency.value} 
                      className="ml-3 flex-grow text-sm text-gray-900 dark:text-gray-100 cursor-pointer"
                    >
                      {currency.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Categories Setting */}
          <motion.div variants={itemVariants}>
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100">
                  Categories
                </h3>
                <div 
                  data-tooltip-id="categories-tooltip"
                  className="text-gray-400 hover:text-gray-600 cursor-help"
                >
                  <FaInfoCircle className="w-4 h-4" />
                </div>
                <Tooltip
                  id="categories-tooltip"
                  place="right"
                  className="max-w-xs bg-gray-800 dark:bg-gray-900 !text-white p-2 rounded-lg shadow-lg"
                >
                  <div className="space-y-2">
                    <p>Categories help you organise your expenses and recurring payments.</p>
                    <ul className="list-disc list-inside text-sm space-y-1 text-gray-300">
                      <li>Used to categorise your spending</li>
                      <li>Helps create budget templates</li>
                      <li>Makes expense tracking easier</li>
                    </ul>
                  </div>
                </Tooltip>
              </div>
            </div>

            {/* Add new category form */}
            <motion.form 
              onSubmit={handleAddCategory}
              className="mb-4 sm:mb-6"
              variants={itemVariants}
            >
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Enter new category"
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border-2 
                    border-gray-300 dark:border-gray-600 rounded-xl 
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 
                    transition-colors dark:text-gray-100 dark:placeholder-gray-400
                    disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isAddingCategory}
                />
                <button
                  type="submit"
                  disabled={isAddingCategory || !newCategory.trim()}
                  className="w-full sm:w-auto px-6 py-2.5 bg-indigo-600 text-white rounded-xl 
                    hover:bg-indigo-700 transition-all duration-200 flex items-center 
                    justify-center gap-2 whitespace-nowrap hover:shadow-lg
                    active:transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAddingCategory ? (
                    <>
                      <FiLoader className="animate-spin" />
                      <span>Adding...</span>
                    </>
                  ) : (
                    <span>Add Category</span>
                  )}
                </button>
              </div>
            </motion.form>

            {/* Categories grid */}
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
              variants={containerVariants}
            >
              <AnimatePresence mode="popLayout">
                {categories.map((category) => (
                  <motion.div
                    key={category}
                    layout
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    whileHover={{ scale: 1.02 }}
                    className={`p-3 rounded-xl transition-all duration-200 
                      ${editingCategory === category 
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 border-2 border-indigo-500' 
                        : 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 border-2 border-transparent'
                      } hover:shadow-md`}
                  >
                    {editingCategory === category ? (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-full flex-1 px-3 py-1.5 bg-white dark:bg-gray-800 border 
                            border-gray-300 dark:border-gray-600 rounded-lg 
                            focus:outline-none focus:ring-2 focus:ring-indigo-500 
                            dark:text-gray-100"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleEditCategory(category, editValue)}
                            className="flex-1 sm:flex-none px-3 py-1.5 bg-indigo-600 text-white rounded-lg 
                              hover:bg-indigo-700 transition-colors"
                          >
                            Save
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setEditingCategory(null)}
                            className="flex-1 sm:flex-none px-3 py-1.5 bg-gray-200 dark:bg-gray-600 
                              text-gray-700 dark:text-gray-200 rounded-lg 
                              hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                          >
                            Cancel
                          </motion.button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-800 dark:text-gray-200 text-sm sm:text-base">
                          {category}
                        </span>
                        <div className="flex items-center gap-2">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => {
                              setEditingCategory(category);
                              setEditValue(category);
                            }}
                            className="p-1.5 text-gray-400 hover:text-indigo-600 
                              dark:hover:text-indigo-400 rounded-lg hover:bg-gray-200 
                              dark:hover:bg-gray-600 transition-colors"
                          >
                            <FiEdit2 className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDeleteCategory(category)}
                            className="p-1.5 text-gray-400 hover:text-red-600 
                              dark:hover:text-red-400 rounded-lg hover:bg-gray-200 
                              dark:hover:bg-gray-600 transition-colors"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>

            {categories.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-6 sm:py-8 text-gray-500 dark:text-gray-400 text-sm sm:text-base"
              >
                No categories yet. Add your first category above.
              </motion.div>
            )}
          </motion.div>

          {/* Language Setting */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Language
            </label>
            <select
              value={settings.language}
              onChange={(e) => handleSettingChange('language', e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option value="en-GB">English (UK)</option>
              <option value="zh-HK">中文 (香港)</option>
            </select>
          </div> */}
        </motion.div>
      </div>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setCategoryToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Category"
        message={`Are you sure you want to delete "${categoryToDelete}"? This action cannot be undone.`}
      />
    </motion.div>
  );
}

export default Settings;